import React from 'react';
import { resolveTokens, type AttributeContext } from '../config/attributes';

/**
 * Runtime resolution of `{{group.key}}` tokens in the runner's authored body text.
 *
 * The report seam (`applyTokens`) and the AI-prompt seam (`renderPrompt`) already compose the shared
 * {@link resolveTokens} resolver, but the step-body render path (section headings/leads via `Block`,
 * guide prose via `GuideText`, the step title/subtitle in `StepHeader`, discovery-question copy) never
 * did — so an authored `{{customer.name}}` leaked to the SE verbatim. This context threads a single
 * per-delivery resolver built once at the step-page level down to those leaves, so the same token means
 * the same thing on screen as it does in the report/PDF/Word/AI grounding.
 *
 * The default is the identity function: a leaf rendered outside a provider (an admin preview, a
 * standalone story) leaves `{{…}}` untouched — the same "verbatim on absence" contract the resolver
 * itself honours, so nothing changes where no delivery context exists.
 */
export type ResolveText = (text: string) => string;

const identity: ResolveText = (text) => text;

const RunnerTokenContext = React.createContext<ResolveText>(identity);

export function RunnerTokenProvider({
  ctx,
  children,
}: {
  ctx: AttributeContext;
  children: React.ReactNode;
}): JSX.Element {
  const resolve = React.useMemo<ResolveText>(() => (text) => resolveTokens(text, ctx), [ctx]);
  return <RunnerTokenContext.Provider value={resolve}>{children}</RunnerTokenContext.Provider>;
}

/** The per-delivery `{{…}}` resolver for the current runner; identity when no provider is mounted. */
export function useResolveText(): ResolveText {
  return React.useContext(RunnerTokenContext);
}

export default RunnerTokenContext;
